/**
 * Operation Process Step Attachment Service
 *
 * 운영 프로세스 단계별 파일/이미지 첨부.
 * 백엔드 filestorage 카테고리 "operation" 위에서 stepId 를 folder_path 로
 * 매핑한다.
 *
 *   {projectId}/uploads/operation/{stepId}/{filename}
 *
 * projectId="template" 은 글로벌 운영 템플릿용 (백엔드에서 admin 권한 필요).
 */

import { apiClient } from './api/apiClient';
import { FileInfo, FileListResponse, UploadResponse } from '../models/FileStorage';

const CATEGORY = 'operation';

export const TEMPLATE_PROJECT_ID = 'template';

/**
 * stepId 검증: path traversal / 슬래시 차단.
 * 백엔드도 검증하지만 UI 에서도 fail-fast.
 */
function assertSafeStepId(stepId: string): void {
  if (!stepId) {
    throw new Error('stepId is required');
  }
  if (stepId.includes('..') || stepId.includes('/') || stepId.includes('\\')) {
    throw new Error(`unsafe stepId: ${stepId}`);
  }
}

function buildUploadUrl(projectId: string, stepId: string): string {
  assertSafeStepId(stepId);
  return `/v0.1/filestorage/${encodeURIComponent(projectId)}/${CATEGORY}/upload?folder_path=${encodeURIComponent(stepId)}`;
}

function buildListUrl(projectId: string): string {
  return `/v0.1/filestorage/${encodeURIComponent(projectId)}/${CATEGORY}/list?page_size=10000`;
}

function buildDownloadUrl(projectId: string, stepId: string, filename: string): string {
  assertSafeStepId(stepId);
  return `/v0.1/filestorage/${encodeURIComponent(projectId)}/${CATEGORY}/download/${encodeURIComponent(stepId)}/${encodeURIComponent(filename)}`;
}

function buildDeleteUrl(projectId: string, stepId: string, filename: string): string {
  assertSafeStepId(stepId);
  return `/v0.1/filestorage/${encodeURIComponent(projectId)}/${CATEGORY}/delete/${encodeURIComponent(stepId)}/${encodeURIComponent(filename)}`;
}

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'];

export function isImageFile(filename: string): boolean {
  const lower = filename.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export interface StepAttachment {
  filename: string;
  size: number;
  uploadDate: string;
  contentType: string;
  uploadedByName?: string;
  isImage: boolean;
}

function toStepAttachment(file: FileInfo): StepAttachment {
  const name = file.original_name || file.filename;
  return {
    filename: name,
    size: file.size_bytes,
    uploadDate: file.upload_date,
    contentType: file.file_type,
    uploadedByName: file.uploaded_by_name,
    isImage: isImageFile(name),
  };
}

export class OperationProcessFileService {
  /**
   * stepId 폴더에 파일 업로드.
   */
  static async upload(
    projectId: string,
    stepId: string,
    files: File[],
    onProgress?: (progress: number) => void,
  ): Promise<StepAttachment[]> {
    if (files.length === 0) {
      return [];
    }
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f, f.name));

    const url = buildUploadUrl(projectId, stepId);
    const res = await apiClient.post<UploadResponse>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (evt) => {
            if (evt.total) {
              onProgress(Math.round((evt.loaded / evt.total) * 100));
            }
          }
        : undefined,
    });

    const uploaded = res.data?.data?.uploaded_files ?? [];
    return uploaded.map(toStepAttachment);
  }

  /**
   * 카테고리 전체 파일 목록을 받아 stepId 폴더에 속한 것만 반환.
   *
   * 백엔드 List 가 폴더 필터를 제공하지 않아서 클라이언트에서 prefix 매칭으로
   * 거른다. 단계 수가 수십개 수준이라 카테고리 전체를 받아도 페이로드는 작음.
   * 장기적으로 부담되면 백엔드에 step_id 필터 추가 검토.
   */
  static async listByStep(projectId: string, stepId: string): Promise<StepAttachment[]> {
    assertSafeStepId(stepId);
    const url = buildListUrl(projectId);
    const res = await apiClient.get<FileListResponse>(url);
    const files = res.data?.data?.files ?? [];

    const prefix = `${stepId}/`;
    return files
      .filter((f) => {
        // path 가 relative path. cctv_id 필드는 첫 폴더 세그먼트와 동일하게
        // 채워지는 케이스가 있어서 (운영 카테고리에선 stepId) 둘 다 검사.
        if (f.cctv_id === stepId) return true;
        if (f.path && f.path.includes(prefix)) return true;
        return false;
      })
      .map(toStepAttachment);
  }

  /**
   * 모든 단계의 첨부 수를 한 번에 가져오기 (대시보드/배지용).
   * stepId -> count
   */
  static async countsByStep(projectId: string): Promise<Record<string, number>> {
    const url = buildListUrl(projectId);
    const res = await apiClient.get<FileListResponse>(url);
    const files = res.data?.data?.files ?? [];

    const counts: Record<string, number> = {};
    for (const f of files) {
      // step 폴더 추출: relative path 의 첫 세그먼트, 없으면 cctv_id (서버에서
      // 첫 세그먼트를 cctv_id 필드에 채워주는 케이스 대응).
      let step = f.cctv_id ?? '';
      if (!step && f.path) {
        const idx = f.path.indexOf('/uploads/operation/');
        if (idx >= 0) {
          const rest = f.path.slice(idx + '/uploads/operation/'.length);
          step = rest.split('/')[0] ?? '';
        }
      }
      if (!step) continue;
      counts[step] = (counts[step] ?? 0) + 1;
    }
    return counts;
  }

  /**
   * 다운로드 URL (브라우저 직접 이동/렌더용).
   * 인증 헤더가 필요해서 a[href] 로 바로 못 쓰고 fetch → blob 변환해야 함.
   */
  static async downloadBlob(projectId: string, stepId: string, filename: string): Promise<Blob> {
    const url = buildDownloadUrl(projectId, stepId, filename);
    const res = await apiClient.get(url, { responseType: 'blob' });
    return res.data as Blob;
  }

  /**
   * 브라우저에서 파일 저장 다이얼로그를 띄우는 헬퍼.
   */
  static async downloadToFile(projectId: string, stepId: string, filename: string): Promise<void> {
    const blob = await this.downloadBlob(projectId, stepId, filename);
    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  /**
   * 이미지 미리보기용 object URL.
   * 호출 측에서 사용 후 URL.revokeObjectURL 로 해제해야 한다.
   */
  static async previewUrl(projectId: string, stepId: string, filename: string): Promise<string> {
    const blob = await this.downloadBlob(projectId, stepId, filename);
    return URL.createObjectURL(blob);
  }

  static async remove(projectId: string, stepId: string, filename: string): Promise<void> {
    const url = buildDeleteUrl(projectId, stepId, filename);
    await apiClient.delete(url);
  }
}
